import React, { useEffect, useState } from 'react';
import {
  Grid,
  Card,
  CardContent,
  CardHeader,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Stack,
  Box,
  Chip,
  Skeleton,
} from '@mui/material';
import SchoolIcon from '@mui/icons-material/School';
import SupervisedUserCircleIcon from '@mui/icons-material/SupervisedUserCircle';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import UpcomingIcon from '@mui/icons-material/Upcoming';
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip } from 'recharts';
import { useSnackbar } from 'notistack';
import DashboardLayout from '../layouts/DashboardLayout';
import StatCard from '../components/StatCard';
import analyticsApi from '../api/analytics';
import { formatDate } from '../utils/format';

const SectionCard = ({ title, subtitle, action, children, contentSx, sx, ...props }) => (
      <Card sx={{ height: '100%', ...sx }} elevation={1} {...props}>
        {(title || subtitle || action) && (
          <CardHeader
            title={
              title ? (
                <Typography variant="h6" sx={{ fontWeight: 800, color: 'text.primary' }}>
                  {title}
                </Typography>
              ) : undefined
            }
            subheader={
              subtitle ? (
                <Typography variant="body2" color="text.secondary">
                  {subtitle}
                </Typography>
              ) : undefined
            }
            action={action}
            sx={{
              alignItems: 'flex-start',
              '& .MuiCardHeader-action': {
                mt: { xs: 2, md: 0 },
                alignSelf: { xs: 'stretch', md: 'center' },
                width: 'auto',
              },
              pb: children ? 0 : 2,
            }}
          />
        )}
        {children ? (
          <CardContent
            sx={{
              pt: title || subtitle || action ? 2 : 3,
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              ...contentSx,
            }}
          >
            {children}
          </CardContent>
        ) : null}
      </Card>
    );

const AdminDashboard = () => {
  const [summary, setSummary] = useState(null);
  const [monthlyActivity, setMonthlyActivity] = useState([]);
  const [educatorBreakdown, setEducatorBreakdown] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const [summaryData, monthly, educators] = await Promise.all([
          analyticsApi.getSummary(),
          analyticsApi.getMonthlyActivity(),
          analyticsApi.getEducatorBreakdown(),
        ]);
        setSummary(summaryData);
        setMonthlyActivity(monthly);
        setEducatorBreakdown(educators);
        setLastUpdated(new Date());
      } catch (err) {
        enqueueSnackbar(err?.response?.data?.message || 'Failed to load analytics data', { variant: 'error' });
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [enqueueSnackbar]);

  return (
    <DashboardLayout title="Admin Overview">
      <Stack spacing={3}>
        <SectionCard
          title="Dashboard Overview"
          subtitle="Key stats from across students, educators, and sessions."
          action={
            <Stack alignItems={{ xs: 'flex-start', md: 'flex-end' }} spacing={0.5}>
              <Typography variant="caption" color="text.secondary">
                Last refreshed
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {lastUpdated ? lastUpdated.toLocaleString() : '-'}
              </Typography>
            </Stack>
          }
          contentSx={{ pt: 0 }}
        >
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1}
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            justifyContent="space-between"
          >
            <Chip
              label={`Snapshot generated on ${formatDate(new Date())}`}
              size="small"
              variant="outlined"
            />
            <Typography variant="caption" color="text.secondary">
              Track learner growth, educator workload, and session health at a glance.
            </Typography>
          </Stack>
        </SectionCard>

        {loading ? (
          <>
            <SectionCard title="Key Metrics">
              <Grid container spacing={2.5}>
                {Array.from({ length: 4 }).map((_, idx) => (
                  <Grid key={idx} item xs={12} sm={6} md={3}>
                    <Skeleton variant="rounded" height={140} />
                  </Grid>
                ))}
              </Grid>
            </SectionCard>
            <Grid container spacing={2.5}>
              <Grid item xs={12} md={8}>
                <Skeleton variant="rounded" height={420} />
              </Grid>
              <Grid item xs={12} md={4}>
                <Skeleton variant="rounded" height={420} />
              </Grid>
            </Grid>
            <SectionCard title="Data Snapshot">
              <Skeleton variant="rounded" height={220} />
            </SectionCard>
          </>
 ) : (
          <>
            <SectionCard title="Key Metrics" subtitle="Current totals across the platform.">
              <Grid container spacing={2.5}>
                <Grid item xs={12} sm={6} md={3}>
                  <StatCard
                    label="Students"
                    value={summary?.studentCount ?? 0}
                    icon={<SchoolIcon />}
                    color="primary"
                    helper="Active learners enrolled"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <StatCard
                    label="Educators"
                    value={summary?.educatorCount ?? 0}
                    icon={<SupervisedUserCircleIcon />}
                    color="secondary"
                    helper="Live educator profiles"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <StatCard
                    label="Total Sessions"
                    value={summary?.sessionCount ?? 0}
                    icon={<EventAvailableIcon />}
                    color="info"
                    helper="Sessions scheduled to date"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <StatCard
                    label="Upcoming Sessions"
                    value={summary?.upcomingCount ?? 0}
                    icon={<UpcomingIcon />}
                    color="warning"
                    helper="Next 30 days"
                  />
                </Grid>
              </Grid>
            </SectionCard>

            <Grid container spacing={2.5}>
              <Grid item xs={12} md={8}>
                <SectionCard
                  title="Monthly Sessions"
                  subtitle="Session volume by month"
                  sx={{ minHeight: 420 }}
                  contentSx={{ height: '100%' }}
                >
                  <Box sx={{ flexGrow: 1, minHeight: 300 }}>
                    {monthlyActivity.length ? (
                      <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={monthlyActivity}>
                          <defs>
                            <linearGradient id="colorSessions" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="#1a237e" stopOpacity={0.8} />
                              <stop offset="95%" stopColor="#1a237e" stopOpacity={0} />
                            </linearGradient>
                          </defs>
                          <XAxis dataKey="month" />
                          <YAxis allowDecimals={false} />
                          <Tooltip />
                          <Area type="monotone" dataKey="count" stroke="#1a237e" fillOpacity={1} fill="url(#colorSessions)" />
                        </AreaChart>
                      </ResponsiveContainer>
                    ) : (
                      <Stack alignItems="center" justifyContent="center" sx={{ height: '100%', color: 'text.secondary' }}>
                        <Typography>No session activity available yet.</Typography>
                      </Stack>
                    )}
                  </Box>
                </SectionCard>
              </Grid>

              <Grid item xs={12} md={4}>
                <SectionCard
                  title="Top Educators"
                  subtitle="Most active educators this term"
                  sx={{ minHeight: 420 }}
                >
                  <List dense sx={{ flexGrow: 1 }}>
                    {educatorBreakdown.length ? educatorBreakdown.map((entry, index) => (
                      <ListItem key={entry.educatorId} divider={index !== educatorBreakdown.length - 1}>
                        <ListItemAvatar>
                          <Avatar>{(entry.educator?.name || 'E')[0]}</Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={`${entry.educator?.name || 'Unknown'} ${entry.educator?.surname || ''}`}
                          secondary={`${entry.sessionCount} session${entry.sessionCount === 1 ? '' : 's'}`}
                        />
                      </ListItem>
                    )) : (
                      <Typography variant="body2" color="text.secondary">
                        No educator data available yet.
                      </Typography>
                    )}
                  </List>
                </SectionCard>
              </Grid>
            </Grid>

            <SectionCard title="Data Snapshot" subtitle="Academic health indicators">
              <Grid container spacing={2}>
                <Grid item xs={12} md={3}>
                  <Typography variant="subtitle2">Average attendance</Typography>
                  <Typography variant="h4" sx={{ fontWeight: 800 }}>{summary?.attendanceRate ?? 0}%</Typography>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Typography variant="subtitle2">Submissions on time</Typography>
                  <Typography variant="h4" sx={{ fontWeight: 800 }}>{summary?.onTimeSubmissions ?? 0}%</Typography>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Typography variant="subtitle2">Assessments created</Typography>
                  <Typography variant="h4" sx={{ fontWeight: 800 }}>{summary?.assessmentCount ?? 0}</Typography>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Typography variant="subtitle2">Active subjects</Typography>
                  <Typography variant="h4" sx={{ fontWeight: 800 }}>{summary?.subjectCount ?? 0}</Typography>
                </Grid>
              </Grid>
              <Typography variant="body2" color="text.secondary">
                Snapshot generated on {formatDate(new Date())}. Use the navigation to manage users, configure sessions,
                and export detailed analytics reports.
              </Typography>
            </SectionCard>
          </>
        )}
      </Stack>
    </DashboardLayout>
  );
};

export default AdminDashboard;
